import { useState, useEffect, useCallback, useContext } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import api from '../../api/api';
import { AuthContext } from '../../context/AuthContext';
import ContributeModal from '../../components/Track/ContributeModal';
import { RESOURCE_ICONS } from './TrackResourcePlayer';
import './TrackDetail.css';
import { getNextStep, nextStepUrl } from '../../utils/trackNavigation';

const DIFFICULTY_META = {
  normal: { label: "Concept Check", color: "#2563eb", bg: "rgba(37,99,235,0.08)" },
  hard: { label: "Deep Assessment", color: "#7c3aed", bg: "rgba(124,58,237,0.08)" },
};

/* ── Readiness Ring SVG ────────────────────────────────────── */
function ReadinessRing({ score = 0, size = 90 }) {
  const r = 36;
  const circ = 2 * Math.PI * r;
  const offset = circ * (1 - score / 100);
  return (
    <div className="td-readiness-ring-wrap">
      <svg width={size} height={size} viewBox="0 0 80 80">
        <defs>
          <linearGradient id="ringGrad" x1="0%" y1="0%" x2="100%" y2="0%">
            <stop offset="0%" stopColor="#2563eb" />
            <stop offset="100%" stopColor="#7c3aed" />
          </linearGradient>
        </defs>
        <circle cx="40" cy="40" r={r} fill="none" stroke="rgba(255,255,255,0.15)" strokeWidth="7" />
        <circle
          cx="40" cy="40" r={r}
          fill="none"
          stroke="url(#ringGrad)"
          strokeWidth="7"
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={offset}
          transform="rotate(-90 40 40)"
          style={{ transition: "stroke-dashoffset 1.2s ease" }}
        />
      </svg>
      <div className="td-readiness-label">
        <span className="td-readiness-val">{score}%</span>
        <span className="td-readiness-sub">readiness</span>
      </div>
    </div>
  );
}

/* ── Pacing Banner ─────────────────────────────────────────── */
function PacingBanner({ pacing }) {
  if (!pacing) return null;
  const { daysLeft, phasesPerWeek, onTrack, remainingPhases } = pacing;
  return (
    <div className={`td-pacing-banner ${onTrack ? "td-pacing-banner--ok" : "td-pacing-banner--warn"}`}>
      <span className="td-pacing-icon">{onTrack ? "📈" : "⚠️"}</span>
      <div className="td-pacing-text">
        {onTrack
          ? `On track! ${remainingPhases} phase${remainingPhases !== 1 ? "s" : ""} left · ${daysLeft} days to deadline`
          : `Behind pace — aim for ${phasesPerWeek} phase${phasesPerWeek !== 1 ? "s" : ""}/week to hit your goal`}
      </div>
      <span className="td-pacing-days">{daysLeft}d left</span>
    </div>
  );
}

/* ── Locked Phase Teaser ───────────────────────────────────── */
function LockedPhaseTeaser({ phase, navigate, isSequentialLock, inline }) {
  return (
    <div className={inline ? "td-locked-teaser-inline" : "td-locked-overlay"}>
      <div className="td-locked-content">
        <span className="td-locked-icon">{isSequentialLock ? '⏳' : '🔒'}</span>
        <p className="td-locked-title">{phase.title}</p>
        {isSequentialLock ? (
          <p className="td-locked-hints">Complete the previous phase to unlock this one.</p>
        ) : (
          <>
            <p className="td-locked-hints">
              {phase.resources?.length > 0 && (
                <span>{phase.resources.length} curated resource{phase.resources.length !== 1 ? "s" : ""}</span>
              )}
              {phase.project && <span> · 1 real-world project</span>}
              {phase.assessmentVideoIds?.length > 0 && <span> · AI assessment</span>}
            </p>
            <button className="td-unlock-btn" onClick={() => navigate('/upgrade')}>
              Unlock with Pro →
            </button>
            <div className="td-referral-alt">
              <span>or</span>
              <button className="td-refer-instead-btn" onClick={() => navigate('/profile#referral')}>
                Refer 1 Friend to Unlock for FREE →
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  );
}

/* ── Sequential Lock Notice (inline, no overlay) ──────────── */
function SequentialLockNotice({ prevPhaseTitle }) {
  return (
    <div className="td-sequential-notice">
      <span className="td-sequential-notice-icon">⏳</span>
      <span>
        Complete <strong>{prevPhaseTitle || "the previous phase"}</strong> to unlock resources and continue.
      </span>
    </div>
  );
}

/* ── Mark Phase Complete button ────────────────────────────── */
function MarkCompleteBtn({ slug, phaseId, quizScore, hasAssessment, onSuccess }) {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const blocked = hasAssessment && (quizScore === null || quizScore < 70);

  const handleClick = async () => {
    if (blocked) return;
    setLoading(true);
    setError(null);
    try {
      await api.post(`/tracks/${slug}/mark-phase-done`, { phaseId });
      onSuccess();
    } catch (err) {
      const code = err.response?.data?.code;
      if (code === "ASSESSMENT_REQUIRED") {
        setError("Pass the assessment first (min 70%)");
      } else {
        setError(err.response?.data?.message || "Error completing phase");
      }
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="td-mark-complete-wrap">
      <button
        className={`td-mark-complete-btn ${blocked ? "td-mark-complete-btn--blocked" : ""}`}
        onClick={handleClick}
        disabled={loading || blocked}
        title={blocked ? "Pass the assessment first" : "Mark this phase as complete"}
      >
        {loading ? "Marking…" : "✓ Mark Phase Complete"}
      </button>
      {error && <p className="td-submit-error">{error}</p>}
    </div>
  );
}

/* ── Deadline Setter ───────────────────────────────────────── */
function DeadlineSetter({ slug, currentDeadline, estimatedMonths, onUpdate }) {
  const [open, setOpen] = useState(false);
  const [val, setVal] = useState(
    currentDeadline
      ? new Date(currentDeadline).toISOString().slice(0, 10)
      : (() => {
        const d = new Date();
        d.setMonth(d.getMonth() + (estimatedMonths || 3));
        return d.toISOString().slice(0, 10);
      })()
  );
  const [saving, setSaving] = useState(false);

  const handleSave = async () => {
    setSaving(true);
    try {
      await api.patch(`/tracks/${slug}/set-deadline`, { goalDeadline: val });
      onUpdate(val);
      setOpen(false);
    } catch {
      // silent
    } finally {
      setSaving(false);
    }
  };

  if (!open) {
    return (
      <button className="td-deadline-trigger" onClick={() => setOpen(true)}>
        {currentDeadline
          ? `🗓 Goal: ${new Date(currentDeadline).toLocaleDateString()}`
          : "🗓 Set goal deadline"}
      </button>
    );
  }

  return (
    <div className="td-deadline-picker">
      <input
        type="date"
        className="td-deadline-input"
        value={val}
        onChange={(e) => setVal(e.target.value)}
        min={new Date().toISOString().slice(0, 10)}
      />
      <button className="td-deadline-save" onClick={handleSave} disabled={saving}>
        {saving ? "…" : "Save"}
      </button>
      <button className="td-deadline-cancel" onClick={() => setOpen(false)}>
        ×
      </button>
    </div>
  );
}

/* ── Main TrackDetail ──────────────────────────────────────── */
export default function TrackDetail() {
  const { slug } = useParams();
  const navigate = useNavigate();
  const { user } = useContext(AuthContext);

  const [trackData, setTrackData] = useState(null);
  const [enrollment, setEnrollment] = useState(null);
  const [loading, setLoading] = useState(true);
  const [enrolling, setEnrolling] = useState(false);
  const [showContributeModal, setShowContributeModal] = useState(false);
  const [deadlineLocal, setDeadlineLocal] = useState(null);
  const [pacingLocal, setPacingLocal] = useState(null);

  const [certificate, setCertificate] = useState(null);

  const fetchTrackData = useCallback(async () => {
    try {
      const { data } = await api.get(`/tracks/${slug}`);
      setTrackData(data.track);
      setEnrollment(data.enrollment || null);
      setDeadlineLocal(data.enrollment?.goalDeadline || null);
      setPacingLocal(data.enrollment?.pacing || null);
    } catch (err) {
      console.error(err);
      navigate('/tracks');
    } finally {
      setLoading(false);
    }
  }, [slug, navigate]);

  useEffect(() => { fetchTrackData(); }, [fetchTrackData]);

  useEffect(() => {
    if (!enrollment?.completed || !trackData?._id) return;
    if (certificate) return;

    const fetchCertificate = async () => {
      try {
        const res = await api.get(`/certificates/${trackData._id}`);
        setCertificate(res.data);
      } catch (err) {
        if (err.response?.status === 404) {
          const res = await api.post(`/certificates/issue/${trackData._id}`);
          setCertificate(res.data);
        } else {
          console.error(err);
        }
      }
    };

    fetchCertificate();
  }, [enrollment?.completed, trackData?._id, certificate]);

  const handleEnroll = async () => {
    if (enrollment || enrolling) return;
    setEnrolling(true);
    try {
      const goalDeadline = new Date(
        Date.now() + (trackData.estimatedMonths || 3) * 30 * 24 * 60 * 60 * 1000
      );
      await api.post(`/tracks/${slug}/enroll`, { goalDeadline });
      await fetchTrackData();
    } catch (err) {
      alert(err.response?.data?.message || "Failed to enroll. Please try again.");
    } finally {
      setEnrolling(false);
    }
  };

  const handleDeadlineUpdate = (newDate) => {
    setDeadlineLocal(newDate);
    fetchTrackData(); // refresh pacing
  };

  if (loading) {
    return (
      <div className="td-page td-page--loading">
        <div className="td-spinner" />
        <p>Loading track…</p>
      </div>
    );
  }

  if (!trackData) return null;

  const phaseCount = trackData.phases?.length || 0;
  const isPro =
    user?.subscription?.plan === "PRO" &&
    (user?.subscription?.status === "ACTIVE" ||
      (user?.subscription?.currentPeriodEnd &&
        new Date(user.subscription.currentPeriodEnd) > new Date()));

  const completedCount = enrollment?.completedPhaseCount || 0;
  const progressPercent = enrollment?.progressPercent || 0;
  const readinessScore = enrollment?.readinessScore || 0;
  const nextStep = enrollment ? getNextStep(trackData, enrollment) : null;
  return (
    <div className="td-page">

      {/* ── Hero ──────────────────────────────────────────────── */}
      <div className="td-hero">
        {trackData.thumbnail ? (
          <div
            className="td-hero-bg"
            style={{ backgroundImage: `url(${trackData.thumbnail})` }}
          >
            <div className="td-hero-overlay" />
          </div>
        ) : (
          <div className="td-hero-bg td-hero-bg--placeholder" />
        )}

        <div className="td-hero-content">
          <div className="td-hero-top">
            <button className="td-back-btn" onClick={() => navigate('/tracks')}>
              ← Tracks
            </button>
            <div className="td-hero-badges">
              <span className="td-category-badge">{trackData.category}</span>
              {trackData.goal && (
                <span className="td-goal-badge">🎯 {trackData.goal}</span>
              )}
            </div>
          </div>

          <h1 className="td-hero-title">{trackData.title}</h1>
          <p className="td-hero-desc">{trackData.description}</p>

          {trackData.prerequisites?.length > 0 && (
            <div className="td-prerequisites">
              <span className="td-prereq-label">Prerequisites:</span>
              <div className="td-prereq-list">
                {trackData.prerequisites.map((pre) => (
                  <button
                    key={pre._id}
                    className="td-prereq-link"
                    onClick={() => navigate(`/tracks/${pre.slug}`)}
                  >
                    {pre.title}
                  </button>
                ))}
              </div>
            </div>
          )}

          <div className="td-hero-stats">
            <span>📚 {phaseCount} phases</span>
            {trackData.estimatedMonths && (
              <span>📅 ~{trackData.estimatedMonths} months</span>
            )}
            <span>✅ Phase 1 free</span>
            <span>👥 {trackData.enrollmentCount} enrolled</span>
            <span id='cert-info'>Get Certified on Completion</span>
          </div>

          {/* Not enrolled CTA */}
          {!enrollment ? (
            <div className="td-enroll-area">
              <button
                className="td-enroll-btn"
                onClick={handleEnroll}
                disabled={enrolling}
              >
                {enrolling ? "Enrolling…" : "Start Track — Free"}
              </button>
              {!isPro && (
                <div className="td-pro-teaser">
                  <span className="td-pro-teaser-tag">PRO</span>
                  <span>Unlock all phases, AI assessments + Proof Profile</span>
                  <button
                    className="td-pro-teaser-btn"
                    onClick={() => navigate('/upgrade')}
                  >
                    Upgrade →
                  </button>
                </div>
              )}
            </div>
          ) : (
            /* Enrolled hero state */
            <div className="td-enrolled-hero">
              <ReadinessRing score={readinessScore} />

              <div className="td-enrolled-info">
                <div className="td-enrolled-badge">
                  <span>✓ Enrolled</span>
                  <span className="td-enrolled-phase">
                    {completedCount === phaseCount ? "Completed 🎉" : `Phase ${Math.min(enrollment.currentPhase, phaseCount)} of ${phaseCount}`}
                  </span>
                </div>

                {/* Progress bar */}
                <div className="td-hero-progress">
                  <div className="td-hero-progress-bar">
                    <div
                      className="td-hero-progress-fill"
                      style={{ width: `${progressPercent}%` }}
                    />
                  </div>
                  <span className="td-hero-progress-label">
                    {completedCount}/{phaseCount} phases complete
                  </span>
                </div>

                <div className="td-enrolled-actions">
                  <DeadlineSetter
                    slug={slug}
                    currentDeadline={deadlineLocal}
                    estimatedMonths={trackData.estimatedMonths}
                    onUpdate={handleDeadlineUpdate}
                  />
                  {enrollment.completed && (
                    <>
                      <button
                        className="td-proof-btn"
                        onClick={() =>
                          navigate(`/u/${user?.username || user?._id}`)
                        }
                      >
                        🏆 View Public Profile →
                      </button>
                      <button
                        className="td-proof-btn"
                        onClick={async () => {
                          const profileUrl = `${window.location.origin}/u/${user?.username || user?._id}`;
                          const url = certificate ? `${window.location.origin}/certificate/${certificate.certificateId}` : profileUrl;
                          try {
                            if (navigator.share) {
                              await navigator.share({ title: "My Learnlia Certificate", text: `I completed ${trackData.title} on Learnlia!`, url });
                            } else {
                              navigator.clipboard.writeText(url);
                              alert("Certificate link copied!");
                            }
                          } catch (err) { }
                        }}
                        style={{ marginLeft: '10px', background: 'var(--accent)', color: '#fff', border: 'none' }}
                      >
                        🔗 Share Certificate
                      </button>
                    </>
                  )}
                  <button
                    className="td-contribute-btn-alt"
                    onClick={() => setShowContributeModal(true)}
                    style={{ marginLeft: enrollment.completed ? '10px' : '0' }}
                  >
                    🚀 Contribute
                  </button>
                </div>

                {/* Continue Learning CTA */}
                {!enrollment.completed && (
                  <div className="td-continue-area">
                    <button
                      className="td-hero-continue-btn"
                      onClick={() => {
                        const url = nextStepUrl(slug, nextStep);
                        navigate(url);
                      }}
                    >
                      Continue Learning →
                    </button>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      </div>

      {/* ── Pacing Banner (enrolled only) ────────────────────── */}
      {enrollment && pacingLocal && (
        <div className="td-body-narrow">
          <PacingBanner pacing={pacingLocal} />
        </div>
      )}

      {/* ── Locked Phases Upgrade Banner (free user, has locked phases) ── */}
      {!isPro && phaseCount > (trackData.freePhaseCount || 1) && (
        <div className="td-upgrade-strip">
          <div className="td-upgrade-strip-inner">
            <div className="td-upgrade-strip-text">
              <strong>
                🔒 {phaseCount - (trackData.freePhaseCount || 1)} phases locked
              </strong>
              <span>
                Upgrade to Pro to unlock all phases, AI concept checks, project
                reviews, and your sharable Proof Profile.
              </span>
            </div>
            <button
              className="td-upgrade-strip-btn"
              onClick={() => navigate('/upgrade')}
            >
              Unlock Everything →
            </button>
          </div>
        </div>
      )}

      {/* ── Timeline ─────────────────────────────────────────── */}
      <div className="td-body">
        <h2 className="td-section-title">Track Curriculum</h2>

        <div className="td-timeline">
          {trackData.phases?.map((phase, index) => {
            const isLocked = phase.locked === true;
            const isFree = index < (trackData.freePhaseCount || 1);
            const isActive =
              enrollment && enrollment.currentPhase === phase.order;

            const prevPhase = index > 0 ? trackData.phases[index - 1] : null;
            const prevCompleted = prevPhase?.phaseProgress?.completed === true;

            // Pro user: previous phase not yet done — show curriculum, block interactions
            const isProSequentialBlock = isLocked && isPro && phase.unlocksAfterPrevious && !prevCompleted;

            // Non-pro paywall lock (keep untouched)
            const isPaywallLocked = isLocked && !isPro;

            // BUG FIX: phaseProgress is now on phase object directly
            const pp = phase.phaseProgress;
            const isCompleted = pp?.completed === true;
            const quizScore = pp?.quizScore ?? null;

            const assessmentMeta =
              DIFFICULTY_META[phase.assessmentDifficulty] ||
              DIFFICULTY_META.normal;
            const hasAssessment =
              phase.assessmentVideoIds?.length > 0 && !isPaywallLocked;

            return (
              <div
                key={phase._id}
                className={[
                  "td-phase",
                  isPaywallLocked ? "td-phase--locked" : "",
                  isProSequentialBlock ? "td-phase--sequential-blocked" : "",
                  isCompleted ? "td-phase--completed" : "",
                  isActive ? "td-phase--active" : "",
                ]
                  .filter(Boolean)
                  .join(" ")}
              >
                {/* Connector */}
                {index < phaseCount - 1 && (
                  <div
                    className={`td-connector ${isCompleted ? "td-connector--done" : ""
                      }`}
                  />
                )}

                {/* Step number */}
                <div className="td-phase-num">
                  {isCompleted ? "✓" : isProSequentialBlock ? "⏳" : isPaywallLocked ? "🔒" : index + 1}
                </div>

                {/* Card */}
                <div className="td-phase-card">
                  <div className="td-phase-header">
                    <div>
                      <div className="td-phase-label-row">
                        <span className="td-phase-order">Phase {phase.order}</span>
                        {isFree && !isPaywallLocked && (
                          <span className="td-free-tag">Free</span>
                        )}
                        {isPaywallLocked && !isPro && (
                          <span className="td-pro-tag">PRO</span>
                        )}
                        {isCompleted && (
                          <span className="td-done-tag">✓ Completed</span>
                        )}
                        {isActive && !isCompleted && (
                          <span className="td-active-tag">← Current</span>
                        )}
                      </div>
                      <h3 className="td-phase-title">{phase.title}</h3>
                    </div>
                    {isCompleted && quizScore !== null && (
                      <div className="td-phase-score-badge">
                        {quizScore}% ✓
                      </div>
                    )}
                  </div>

                  <p className="td-phase-desc">{phase.description}</p>

                  {/* Resources — shown as preview for locked phases */}
                  {phase.resources?.length > 0 && (
                    <div className="td-resources">
                      {phase.resources.map((r, ri) => {
                        // Title logic: Locked phases get essential titles, unlocked get labels
                        const displayTitle = isPaywallLocked
                          ? (r.title || r.courseId?.title || r.label)
                          : r.label;

                        return (
                          <div key={ri} className={`td-resource ${pp?.completedResources?.includes(ri) ? 'td-resource--done' : ''} ${isPaywallLocked ? 'td-resource--locked-preview' : ''}`}>
                            <span className="td-resource-icon">
                              {RESOURCE_ICONS[r.type] || "🔗"}
                            </span>
                            <div className="td-resource-info">
                              <span className="td-resource-label">
                                {displayTitle}
                                {pp?.completedResources?.includes(ri) && <span className="td-res-done"> ✓</span>}
                              </span>
                              {r.estimatedMinutes > 0 && (
                                <span className="td-resource-time">
                                  {r.estimatedMinutes}m
                                </span>
                              )}
                            </div>

                            <button
                              className={`td-resource-action-btn ${isPaywallLocked || isProSequentialBlock ? 'td-resource-action-btn--sequential-locked' : ''}`}
                              disabled={isPaywallLocked || isProSequentialBlock}
                              onClick={(e) => {
                                e.stopPropagation();
                                if (isPaywallLocked || isProSequentialBlock) return;
                                navigate(`/tracks/${slug}/resource/${phase._id}/${ri}`);
                              }}
                            >
                              {isPaywallLocked ? "🔒" : isProSequentialBlock ? "⏳" : "Open →"}
                            </button>

                          {process.env.REACT_APP_SHOW_CONTRIBUTOR_CREDIT === "true" &&
                            r.contributedBy && (
                              <div
                                className="td-contributor-credit"
                                title="Contributed by the community"
                              >
                                ✦ {r.contributedBy.name}
                              </div>
                            )}
                        </div>
                      );
                    })}
                  </div>
                )}

                  {/* Project — shown for pro sequential block but button hidden */}
                  {!isPaywallLocked && phase.project && (
                    <div className="td-project">
                      <div className="td-project-header">
                        <span>🚀</span>
                        <span className="td-project-label">Project</span>
                        {pp?.projectLogged && (
                          <span className="td-project-submitted">Submitted ✓</span>
                        )}
                      </div>
                      <p className="td-project-title">{phase.project.title}</p>
                      <p className="td-project-desc">
                        {phase.project.description}
                      </p>
                      {phase.project.requiredSkills?.length > 0 && (
                        <div className="td-project-skills">
                          {phase.project.requiredSkills.map((skill) => (
                            <span key={skill} className="td-skill-chip">
                              {skill}
                            </span>
                          ))}
                        </div>
                      )}

                      {/* Hide project button when sequentially blocked */}
                      {enrollment && !isPaywallLocked && !isProSequentialBlock && (
                        <div className="td-project-footer">
                          <button
                            className="td-project-open-btn"
                            onClick={() => navigate(`/tracks/${slug}/project/${phase._id}`)}
                          >
                            View Project & Submit →
                          </button>
                        </div>
                      )}

                      {pp?.projectUrl && (
                        <div className="td-project-current-url">
                          Already Submitted: <a href={pp.projectUrl} target="_blank" rel="noreferrer">View Link ↗</a>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Assessment section — hidden when sequentially blocked */}
                  {hasAssessment && enrollment && !isProSequentialBlock && (
                    <div className="td-assessment-section">
                      <div
                        className="td-assessment-chip"
                        style={{
                          background: assessmentMeta.bg,
                          color: assessmentMeta.color,
                        }}
                      >
                        🤖 AI {assessmentMeta.label}
                      </div>

                      {isCompleted ? (
                        <div className="td-assessment-passed">
                          ✓ Assessment Passed: <strong>{quizScore}%</strong>
                        </div>
                      ) : (
                        <div className="td-assessment-cta">
                          {quizScore !== null ? (
                            <div className="td-score-status">
                              Last score:{" "}
                              <strong
                                className={
                                  quizScore < 70
                                    ? "td-score-fail"
                                    : "td-score-ok"
                                }
                              >
                                {quizScore}%
                              </strong>
                              {quizScore < 70 && (
                                <span className="td-score-fail">
                                  {" "}(need 70%)
                                </span>
                              )}
                            </div>
                          ) : (
                            <p className="td-assessment-hint">
                              Complete the AI assessment to unlock phase
                              completion.
                            </p>
                          )}
                          <button
                            className="td-take-assessment-btn"
                            onClick={() =>
                              navigate(
                                `/tracks/${slug}/assessment/${phase._id}`
                              )
                            }
                          >
                            {quizScore !== null
                              ? "Retake Assessment"
                              : "Take Assessment →"}
                          </button>
                        </div>
                      )}
                    </div>
                  )}

                  {/* Sequential lock notice (inline, replaces overlay for Pro users) */}
                  {isProSequentialBlock && (
                    <SequentialLockNotice prevPhaseTitle={prevPhase?.title} />
                  )}

                  {/* Explicit Mark Complete button — hidden when sequentially blocked */}
                  {enrollment && !isCompleted && !isPaywallLocked && !isProSequentialBlock && (
                    <MarkCompleteBtn
                      slug={slug}
                      phaseId={phase._id}
                      quizScore={quizScore}
                      hasAssessment={hasAssessment}
                      onSuccess={fetchTrackData}
                    />
                  )}

                  {/* Locked overlay — paywall lock only (non-pro users) */}
                  {isPaywallLocked && (
                    <LockedPhaseTeaser phase={phase} navigate={navigate} isSequentialLock={false} inline={true} />
                  )}
                </div>
              </div>
            );
          })}
        </div>

        {/* Track completed state */}
        {enrollment?.completed && (
          <div className="td-completed-banner">
            <div className="td-completed-icon">🏆</div>
            <div>
              <strong>Track Complete!</strong>
              <p>
                You've finished every phase with a readiness score of{" "}
                <strong>{enrollment.readinessScore}%</strong>. Your Proof
                Profile is now public.
              </p>
            </div>

            <div style={{ marginTop: '1rem', padding: '1rem', background: 'rgba(34, 197, 94, 0.1)', borderRadius: '8px', border: '1px solid rgba(34, 197, 94, 0.2)' }}>
              <h4 style={{ margin: '0 0 0.5rem 0', color: '#22c55e' }}>🎉 Outstanding Achievement!</h4>
              <p style={{ margin: 0, fontSize: '0.9rem', color: 'var(--text-secondary)' }}>
                You've mastered this track! Keep the momentum going by starting another track to expand your skills, or share your success with your network!
              </p>
            </div>

            <div className="td-actions-row" style={{ display: 'flex', gap: '1rem', marginTop: '1rem', flexWrap: 'wrap' }}>
              <button
                className="td-proof-btn td-proof-btn--large"
                onClick={() =>
                  navigate(`/u/${user?.username || user?._id}`)
                }
              >
                🏆 View Public Profile →
              </button>
              {certificate && (
                <button
                  className="td-proof-btn td-proof-btn--large"
                  style={{ background: '#22c55e', color: '#fff' }}
                  onClick={() => navigate(`/certificate/${certificate.certificateId}`)}
                >
                  📜 Download Certificate
                </button>
              )}
            </div>
          </div>
        )}
      </div>

      {showContributeModal && (
        <ContributeModal
          slug={slug}
          track={trackData}
          phases={trackData.phases || []}
          onClose={() => setShowContributeModal(false)}
        />
      )}

    </div>
  );
}